/**
 * Budgets.
 *
 * ═══════════════════════════════════════════════════════════════════
 * AVAILABLE IS NOT BUDGET LESS SPENT.
 *
 * It is budget, less what has been spent, less what is already committed. A
 * budget holder who ignores commitments will overspend, because the money on
 * order has already gone in every sense except the accounting one.
 *
 * So this screen shows all four columns and never hides the commitment. The
 * variance is against the budget PROFILED TO DATE, not against the annual
 * figure — comparing October's spend with a year's budget tells nobody
 * anything.
 * ═══════════════════════════════════════════════════════════════════
 *
 * Every spend figure here leads somewhere. "What is that £8m?" is the first
 * question anybody asks of a budget report, and the answer is the general
 * ledger enquiry filtered to the row that was clicked — which rolls up through
 * the cost-centre tree exactly as the figure on the row did.
 */

import { useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../lib/api';
import { useAsync, useMutation } from '../lib/useAsync';
import { useSession } from '../lib/session';
import { useTerms, lower } from '../lib/tenant';
import { money, percent, utilisationTextClass } from '../lib/format';
import { Alert, AsyncPanel, Card, DataTable, EmptyState, ErrorNotice, Money, PageHeader, Progress, Stat } from '../components/ui';

interface BudgetActualRow {
  costCentreId: string;
  costCentreCode: string;
  costCentreName: string;
  levelType: string;
  depth: number;
  accountId: string | null;
  accountCode: string | null;
  accountName: string | null;
  annualBudgetMinor: number;
  budgetToDateMinor: number;
  actualToDateMinor: number;
  commitmentMinor: number;
  availableMinor: number;
  varianceMinor: number;
  variancePercent: number | null;
  utilisationPercent: number;
}

interface BudgetReport {
  meta: { reportName: string; entityName: string; runBy: string; runAtDisplay: string; parameters: string | null; dataNotice: string };
  rows: BudgetActualRow[];
  totals: Omit<BudgetActualRow, 'costCentreId' | 'costCentreCode' | 'costCentreName' | 'levelType' | 'depth' | 'accountId' | 'accountCode' | 'accountName'> | null;
  message?: string;
}

/** The reference lookups answer in camelCase; these are the fields this screen reads. */
interface AccountingPeriod {
  id: string;
  name: string;
  periodNo: number;
  isAdjustmentPeriod: boolean;
}

/**
 * The variance percentage, turned round to agree with the variance in pounds.
 *
 * The server states the two the opposite way about: the money variance is
 * budget less spend, so an underspend is positive, while the percentage is
 * spend against budget, so the same underspend is negative. Printed one above
 * the other they contradict each other and the reader cannot tell which is
 * wrong. The percentage is therefore flipped here to the money's convention —
 * positive is an underspend — and the minus sign is replaced with the
 * typographic one `money()` uses, so a negative amount and a negative
 * percentage in the same cell look like the same kind of thing.
 */
function varianceToDatePercent(value: number | null): string | null {
  if (value === null) return null;
  return percent(-value).replace('-', '−');
}

export function BudgetsPage() {
  const t = useTerms();

  const years = useAsync(
    () =>
      api.get<{ financialYears: Array<{ id: string; code: string; name: string; status: string }>; currentFinancialYearId: string }>(
        '/reference/financial-years',
      ),
    [],
  );

  return (
    <>
      <PageHeader
        title="Budgets"
        subtitle={`Budget, spent, committed and available for every ${lower(t.costCentre)} you may see.`}
      />

      {/*
        The report cannot be asked for until the current financial year is
        known, so the screen waits on that lookup rather than resolving it to
        nothing. A lookup that fails has to read as a failure with a way to try
        again — not as an organisation that has no budgets.
      */}
      <AsyncPanel state={years}>
        {(data) =>
          data.currentFinancialYearId ? (
            <BudgetVersusActual financialYearId={data.currentFinancialYearId} />
          ) : (
            <Alert tone="warn" title="No open financial year">
              <p>
                No financial year is open for this {lower(t.entity)}, so there is no budget to report against yet.
              </p>
            </Alert>
          )
        }
      </AsyncPanel>
    </>
  );
}

function BudgetVersusActual({ financialYearId }: { financialYearId: string }) {
  const t = useTerms();
  const { can } = useSession();
  const [byAccount, setByAccount] = useState(false);
  const [periodId, setPeriodId] = useState('');

  const periods = useAsync(
    () => api.get<{ periods: AccountingPeriod[] }>(`/reference/periods?financialYearId=${financialYearId}`),
    [financialYearId],
  );

  const report = useAsync(
    () =>
      api.get<BudgetReport>(
        `/reports/budget-vs-actual?${new URLSearchParams({
          financialYearId,
          ...(periodId ? { periodId } : {}),
          byAccount: String(byAccount),
        })}`,
      ),
    [financialYearId, periodId, byAccount],
  );

  /*
   * The export is run as a mutation, not fired and forgotten.
   *
   * It posts the rows on screen to the server's CSV writer, and that request
   * can fail. Discarding the promise made a failed export look exactly like a
   * successful one — no file, no message, nothing — so the presenter could not
   * tell whether to press it again.
   */
  const exportCsv = useMutation((data: BudgetReport) =>
    api.exportCsv({
      reportName: 'Budget versus actual',
      parameters: data.meta.parameters ?? undefined,
      columns: [
        { key: 'costCentreCode', label: t.costCentre },
        { key: 'costCentreName', label: 'Name' },
        ...(byAccount
          ? [
              { key: 'accountCode', label: 'Nominal code' },
              { key: 'accountName', label: 'Nominal name' },
            ]
          : []),
        { key: 'annualBudgetMinor', label: 'Annual budget', type: 'money' as const },
        { key: 'budgetToDateMinor', label: 'Budget to date', type: 'money' as const },
        { key: 'actualToDateMinor', label: 'Actual', type: 'money' as const },
        { key: 'commitmentMinor', label: 'Committed', type: 'money' as const },
        { key: 'availableMinor', label: 'Available', type: 'money' as const },
        { key: 'varianceMinor', label: 'Variance', type: 'money' as const },
      ],
      rows: data.rows as unknown as Array<Record<string, unknown>>,
    }),
  );

  /*
   * Both are drawn only for somebody the server will actually let through: the
   * ledger enquiry needs `report.run`, and the CSV writer needs
   * `report.export`, which an ordinary department user does not hold. A figure
   * that links to a refusal, or a button that answers 403, is worse than no
   * link and no button.
   */
  const canDrill = can('report.run');
  const canExport = can('report.export');

  const periodOptions = periods.data?.periods ?? [];

  /** The postings behind one row. The enquiry rolls a cost centre up through its children, as the figure does. */
  const ledgerFor = (row: BudgetActualRow) =>
    `/reports/GENERAL_LEDGER?${new URLSearchParams({
      costCentreId: row.costCentreId,
      ...(row.accountId ? { accountId: row.accountId } : {}),
      financialYearId,
    })}`;

  return (
    <AsyncPanel state={report}>
      {(data) => {
        if (data.message) {
          return (
            <Alert tone="warn" title="No budget to report against">
              <p>{data.message}</p>
            </Alert>
          );
        }

        /*
         * Indentation is measured from the shallowest row in this result, not
         * from the top of the tree. A budget holder's report begins at their
         * own department, and without this every row of it would sit indented
         * under a parent they are not allowed to see.
         */
        const baseDepth = data.rows.reduce((shallowest, r) => Math.min(shallowest, r.depth), Number.MAX_SAFE_INTEGER);

        return (
          <div className="stack">
            {data.totals ? (
              <div className="grid grid--4">
                <Stat label="Annual budget" value={money(data.totals.annualBudgetMinor)} />
                <Stat
                  label="Spent to date"
                  value={money(data.totals.actualToDateMinor)}
                  detail={`Budget to date ${money(data.totals.budgetToDateMinor)}`}
                  to={canDrill ? `/reports/GENERAL_LEDGER?financialYearId=${financialYearId}` : undefined}
                  drillLabel="See the postings"
                />
                <Stat
                  label="Committed"
                  value={money(data.totals.commitmentMinor)}
                  detail="On order or requested, not yet invoiced"
                  to={canDrill ? '/reports/COMMITMENTS' : undefined}
                  drillLabel="See the commitments"
                />
                <Stat
                  label="Available"
                  value={money(data.totals.availableMinor)}
                  tone={data.totals.availableMinor >= 0 ? 'positive' : 'negative'}
                  detail={
                    <>
                      <Progress percent={data.totals.utilisationPercent} label="Budget used" />
                      {percent(data.totals.utilisationPercent)} used
                    </>
                  }
                />
              </div>
            ) : null}

            {/* Sits next to the button that caused it, and disappears on the next attempt. */}
            <ErrorNotice error={exportCsv.error} />

            <Card
              title={byAccount ? `By ${lower(t.costCentre)} and nominal code` : `By ${lower(t.costCentre)}`}
              subtitle={data.meta.parameters ?? undefined}
              flush
              actions={
                <div className="row" style={{ gap: '0.75rem' }}>
                  {/*
                    The period picker appears once the lookup has answered. If
                    it fails the report still stands on the current period,
                    which the card subtitle names — better than an error panel
                    wedged into a card header, and better than a select whose
                    only option changes nothing.
                  */}
                  {periodOptions.length > 0 ? (
                    <label className="row" style={{ gap: '0.4rem' }}>
                      <span className="smaller muted">Period</span>
                      <select value={periodId} onChange={(e) => setPeriodId(e.currentTarget.value)}>
                        <option value="">Current</option>
                        {periodOptions.map((period) => (
                          <option key={period.id} value={period.id}>
                            {period.name}
                          </option>
                        ))}
                      </select>
                    </label>
                  ) : null}
                  <label className="checkbox-row">
                    <input type="checkbox" checked={byAccount} onChange={(e) => setByAccount(e.currentTarget.checked)} />
                    <span className="smaller">Split by nominal code</span>
                  </label>
                  {canExport && data.rows.length > 0 ? (
                    <button
                      type="button"
                      className="btn btn--sm"
                      disabled={exportCsv.busy}
                      onClick={() => void exportCsv.run(data)}
                    >
                      {exportCsv.busy ? 'Exporting…' : 'Export'}
                    </button>
                  ) : null}
                </div>
              }
            >
              {data.rows.length === 0 ? (
                <div style={{ padding: '1.5rem' }}>
                  <EmptyState title="No budget lines to show">
                    <p>
                      Nothing has been budgeted against the {lower(t.costCentrePlural)} you may see for this period.
                      Try another period, or ask {t.financeTeam} whether the budget for your area has been published.
                    </p>
                  </EmptyState>
                </div>
              ) : (
                <DataTable
                  caption="Budget against actual"
                  rows={data.rows}
                  rowKey={(r, i) => `${r.costCentreId}-${r.accountId ?? 'all'}-${i}`}
                  /*
                   * Paged only when split by nominal code, where the report
                   * runs to several hundred lines and the total row would
                   * otherwise be an unbroken scroll away from the figures it
                   * totals. Not sortable and not searchable, deliberately:
                   * these rows are a hierarchy that adds up, and reordering or
                   * filtering them would leave the total below contradicting
                   * the rows above it.
                   */
                  pageSize={byAccount ? 50 : undefined}
                  columns={[
                    {
                      key: 'cc',
                      header: t.costCentre,
                      render: (r) => {
                        const step = Math.max(0, r.depth - baseDepth);
                        // A row that has children is a roll-up of them, and reads as one.
                        const rollUp = r.levelType !== 'cost_centre';
                        return (
                          <span
                            style={{
                              display: 'inline-block',
                              paddingLeft: step > 0 ? `${0.5 + step}rem` : undefined,
                              fontWeight: rollUp ? 600 : undefined,
                            }}
                          >
                            <span className="mono smaller">{r.costCentreCode}</span> {r.costCentreName}
                            {r.accountCode ? (
                              <div className="smaller muted" style={{ fontWeight: 400 }}>
                                <span className="mono">{r.accountCode}</span> {r.accountName}
                              </div>
                            ) : null}
                          </span>
                        );
                      },
                    },
                    {
                      key: 'annual',
                      header: 'Annual budget',
                      numeric: true,
                      render: (r) => <Money minor={r.annualBudgetMinor} blankIfZero />,
                    },
                    {
                      key: 'todate',
                      header: 'Budget to date',
                      numeric: true,
                      render: (r) => <Money minor={r.budgetToDateMinor} blankIfZero />,
                    },
                    {
                      key: 'actual',
                      header: 'Spent',
                      numeric: true,
                      // Nothing spent means an empty cell, and an empty cell is
                      // nothing to click, so the link is only drawn on a figure.
                      render: (r) =>
                        canDrill && r.actualToDateMinor !== 0 ? (
                          <Link
                            to={ledgerFor(r)}
                            title={`Every posting behind this figure for ${r.costCentreCode} ${r.costCentreName}`}
                          >
                            <Money minor={r.actualToDateMinor} blankIfZero />
                          </Link>
                        ) : (
                          <Money minor={r.actualToDateMinor} blankIfZero />
                        ),
                    },
                    {
                      key: 'committed',
                      header: 'Committed',
                      numeric: true,
                      render: (r) => <Money minor={r.commitmentMinor} blankIfZero />,
                    },
                    {
                      key: 'available',
                      header: 'Available',
                      numeric: true,
                      render: (r) => <Money minor={r.availableMinor} bold />,
                    },
                    {
                      key: 'variance',
                      header: 'Variance to date',
                      numeric: true,
                      render: (r) => (
                        <>
                          <Money minor={r.varianceMinor} blankIfZero />
                          {r.variancePercent !== null ? (
                            <div className="smaller muted">{varianceToDatePercent(r.variancePercent)}</div>
                          ) : null}
                        </>
                      ),
                    },
                    {
                      key: 'used',
                      header: 'Used',
                      width: '9rem',
                      render: (r) => (
                        <>
                          <Progress percent={r.utilisationPercent} label={`${r.costCentreName} budget used`} />
                          <span className={`smaller ${utilisationTextClass(r.utilisationPercent)}`}>
                            {percent(r.utilisationPercent)}
                          </span>
                        </>
                      ),
                    },
                  ]}
                  footer={
                    data.totals ? (
                      <tr className="is-total">
                        <td>Total</td>
                        <td className="num">
                          <Money minor={data.totals.annualBudgetMinor} />
                        </td>
                        <td className="num">
                          <Money minor={data.totals.budgetToDateMinor} />
                        </td>
                        <td className="num">
                          <Money minor={data.totals.actualToDateMinor} />
                        </td>
                        <td className="num">
                          <Money minor={data.totals.commitmentMinor} />
                        </td>
                        <td className="num">
                          <Money minor={data.totals.availableMinor} />
                        </td>
                        <td className="num">
                          <Money minor={data.totals.varianceMinor} />
                          {data.totals.variancePercent !== null ? (
                            <div className="smaller">{varianceToDatePercent(data.totals.variancePercent)}</div>
                          ) : null}
                        </td>
                        <td className="num">{percent(data.totals.utilisationPercent)}</td>
                      </tr>
                    ) : null
                  }
                />
              )}
            </Card>

            <p className="smaller muted">
              <strong>Available</strong> is the annual budget less what has been spent and less what is already
              committed. <strong>Variance to date</strong> compares spend with the budget profiled to this point in
              the year, not with the annual figure; a positive variance is an underspend.
            </p>
            <p className="smaller muted">
              Run by {data.meta.runBy} at {data.meta.runAtDisplay}. {data.meta.dataNotice}
            </p>
          </div>
        );
      }}
    </AsyncPanel>
  );
}
